import CardState, { type CardStateValue } from '@/components/ui/CardState'
import Change from '@/components/ui/Change'
import Money from '@/components/ui/Money'
import SectionHead from '@/components/ui/SectionHead'
import Skeleton from '@/components/ui/Skeleton'
import { useCommodityPrices } from '@/features/market-rail/api/queries'
import RailRows from '@/features/market-rail/components/RailRows'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { cn } from '@/lib/utils'
import { sign } from '@/lib/money'
import type { CommodityKind, CommodityPrice } from '@/schemas/commodities'

/** The mock draws four; mm-be sends seven, fine gold, silver, petrol and LPG first. */
const RAIL_ROWS = 4

/**
 * The four marks of the mock's `price-icon`: an ingot for gold and silver, a drop, a cylinder.
 * 24-box line art at 1.6 stroke, drawn in `currentColor`; gold wears the accent, the rest the
 * subtle grey.
 */
const ICON_PATHS: Record<CommodityKind, readonly string[]> = {
  gold: ['M4 17h16l-3-7H7l-3 7ZM9 10l1.5-4h3L15 10'],
  silver: ['M4 17h16l-3-7H7l-3 7ZM9 10l1.5-4h3L15 10'],
  fuel: ['M12 3.5c3 4 5.5 7 5.5 10a5.5 5.5 0 0 1-11 0c0-3 2.5-6 5.5-10Z'],
  gas: ['M7 9a5 5 0 0 1 10 0v9a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V9Z', 'M10 4h4M12 4v2'],
}

function CommodityIcon({ kind }: { kind: CommodityKind }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn(
        'size-5 shrink-0 fill-none stroke-current',
        kind === 'gold' ? 'text-accent' : 'text-muted-foreground-subtle',
      )}
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICON_PATHS[kind].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}

/**
 * One commodity row (`rail-com__row`): the mark, the name over its unit, the price over the move
 * since the previous quote. Name 14/400 in the secondary grey, unit 12 in the subtle grey, price 14/600 in the
 * foreground, the move 12 in the market colours or a plain "Same". The price is always whole
 * rupees; the MOVE is quoted in the commodity's own decimals (a tola of gold moves by whole
 * rupees, a litre of petrol by paisa).
 *
 * Not a link and carries no hover wash: the mock's row opens a per-commodity page, and there is no
 * such page (the sidebar's Commodities stays "Soon"), so nothing here should look pressable.
 */
function CommodityRow({ item }: { item: CommodityPrice }) {
  const move = item.change
  const whole = item.deltaDecimals === 0

  return (
    <div className="flex items-center gap-3 border-b border-border-subtle py-2 last:border-b-0">
      <CommodityIcon kind={item.kind} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body-m text-muted-foreground">{item.name}</span>
        {item.unit === null ? null : (
          <span className="truncate text-body-xs text-muted-foreground-subtle">{item.unit}</span>
        )}
      </span>
      <span className="flex flex-col items-end">
        <Money value={item.price} currency whole className="text-h-s text-foreground" />
        {move === null ? null : sign(move) === 0 ? (
          <span className="text-body-xs text-muted-foreground-subtle">Same</span>
        ) : (
          <Change value={move} whole={whole} className="text-body-xs" />
        )}
      </span>
    </div>
  )
}

function CommodityRowSkeleton() {
  return (
    <div className="flex items-center gap-3 py-2">
      <Skeleton className="size-5 rounded-full" />
      <div className="flex flex-1 flex-col gap-1">
        <Skeleton className="h-4 w-20 rounded-xs" />
        <Skeleton className="h-4 w-14 rounded-xs" />
      </div>
      <div className="flex flex-col items-end gap-1">
        <Skeleton className="h-4 w-16 rounded-xs" />
        <Skeleton className="h-4 w-10 rounded-xs" />
      </div>
    </div>
  )
}

const SKELETON_ROWS = Array.from({ length: RAIL_ROWS }, (_, index) => (
  <CommodityRowSkeleton key={index} />
))

/**
 * The rail's "Prices today" block (Home mock parity, 2026-10-28): the first four of
 * `GET /api/v5/commodities/`, each with its unit, its price and the move since the previous
 * quote. With no "All prices" page to open (Commodities is "Soon" in the sidebar) the head
 * carries no link; one comes with the page.
 *
 * Rows draw through `RailRows` like every rail list, so the fit trims them on a short window.
 */
export default function PricesTodayCard() {
  const block = useRailBlock('prices')
  const prices = useCommodityPrices()
  const { data, isPending, isError } = prices
  const manual = useManualRefresh([prices])

  const state: CardStateValue = isPending
    ? 'loading'
    : isError
      ? 'failed'
      : data.length === 0
        ? 'empty'
        : manual.isManual
          ? 'refreshing'
          : 'loaded'

  return (
    <section aria-label="Prices today" {...block}>
      <SectionHead size="sm" title="Prices today" />
      <div className="mt-1">
        <CardState
          state={state}
          skeleton={<RailRows block="prices" rows={SKELETON_ROWS} />}
          onRetry={manual.refresh}
          failedTitle="Couldn't load prices"
          failedDescription="Today's prices are not coming through. Try again."
          emptyTitle="No prices today"
          emptyDescription="The day's quotes show here once they are published."
        >
          {data === undefined ? null : (
            <RailRows
              block="prices"
              rows={data.slice(0, RAIL_ROWS).map((item) => (
                <CommodityRow key={item.key} item={item} />
              ))}
            />
          )}
        </CardState>
      </div>
    </section>
  )
}
